import ImportLayout from '@app/components/imports/import-layout';
import GoodsImportTypePage from '@app/components/imports/goods-import/pages/goods-import-type-page';
import GoodsImportIntroPage from '@app/components/imports/goods-import/pages/goods-import-intro-page';
import GoodsImportConfigurePage from '@app/components/imports/goods-import/pages/goods-import-configure-page';
import { CustomRoute } from '@app/components/layout/sidebar/components/route';
import CreateReportIntroPage from '@app/components/reports/create-report/create-report-intro-page';
import CreateReportMetricPage from '@app/components/reports/create-report/create-report-metric-page';
import CreateReportPublishPage from '@app/components/reports/create-report/create-report-publish';
import CreateReportSetupPage from '@app/components/reports/create-report/create-report-setup-page';
import PurchaseReportsListPage from '@app/purchased-goods/report/purchase-reports-list-page';
import { FEATURES } from '@app/utils/constants';
import { OperationReportTypeEnum } from '@shared/api/types';
import { Outlet, Route } from 'react-router-dom';
import CreateReportLayout from '../components/reports/create-report/create-report-layout';
import PurchasedFoodAndDrinkDataCoveragePage from './food-and-drink/pages/purchased-food-and-drink-data-coverage-page';
import PurchasedFoodAndDrinkIngredientsPage from './food-and-drink/pages/purchased-food-and-drink-ingredients-page';
import PurchasedFoodAndDrinkItemOverviewPage from './food-and-drink/pages/purchased-food-and-drink-item-overview-page';
import PurchasedFoodAndDrinkItemPage from './food-and-drink/pages/purchased-food-and-drink-item-page';
import PurchasedFoodAndDrinkItemPurchasesPage from './food-and-drink/pages/purchased-food-and-drink-item-purchases-page';
import PurchasedFoodAndDrinkItemSpendPage from './food-and-drink/pages/purchased-food-and-drink-item-spend-page';
import PurchasedFoodAndDrinkListPage from './food-and-drink/pages/purchased-food-and-drink-list-page';
import PurchasedFoodAndDrinkReviewPage from './food-and-drink/pages/purchased-food-and-drink-review-page';
import PurchasedFoodAndDrinkItemContext from './food-and-drink/purchased-food-and-drink-item-context';
import PurchasedFoodAndDrinkItemLayout from './food-and-drink/purchased-food-and-drink-item-layout';
import PurchasedFoodAndDrinkLayout from './food-and-drink/purchased-food-and-drink-layout';
import PurchasedPackagingDataCoveragePage from './packaging/pages/purchased-packaging-coverage-page';
import PurchasedPackagingItemOverviewPage from './packaging/pages/purchased-packaging-item-overview-page';
import PurchasedPackagingItemPage from './packaging/pages/purchased-packaging-item-page';
import PurchasedPackagingItemPurchasesPage from './packaging/pages/purchased-packaging-item-purchases-page';
import PurchasedPackagingItemSpendPage from './packaging/pages/purchased-packaging-item-spend-page';
import PurchasedPackagingListPage from './packaging/pages/purchased-packaging-list-page';
import PurchasedPackagingItemContext from './packaging/purchased-packaging-item-context';
import PurchasedPackagingItemLayout from './packaging/purchased-packaging-item-layout';
import PurchasedPackagingLayout from './packaging/purchased-packaging-layout';
import PurchasingInsightsPage from './insights/pages/purchasing-insights-page';
import PurchasedGoodsImportsListPage from './pages/purchased-goods-imports-list-page';
import PurchaseReport from './report/purchase-report';
import PurchaseReportDownload from './report/purchase-report-download';
import PurchaseReportContextProvider from './report/purchase-report-result-context';
import ExcludedItemsCategoryPage from './excluded-items/pages/excluded-items-category-page';
import ExcludedItemsLandingPage from './excluded-items/pages/excluded-items-landing-page';

export const purchasedGoodsRoutes = {
  // general
  PURCHASED_GOODS: '/purchased-goods',
  PURCHASED_INSIGHTS: '/purchased-goods/insights',
  PURCHASED_GOODS_IMPORTS_LIST: '/purchased-goods/imports',

  // unified import flow (landing → activity types → organise + upload)
  PURCHASED_GOODS_IMPORT: '/purchased-goods/import',
  PURCHASED_GOODS_IMPORT_TYPES: '/purchased-goods/import/types',
  PURCHASED_GOODS_IMPORT_ORGANISE: '/purchased-goods/import/organise',

  // excluded items (Uncertain + Other goods)
  PURCHASED_EXCLUDED_ITEMS: '/purchased-goods/excluded-items',
  PURCHASED_EXCLUDED_ITEMS_REVIEW: '/purchased-goods/excluded-items/review',
  PURCHASED_EXCLUDED_ITEMS_OTHER: '/purchased-goods/excluded-items/other',

  // reports
  PURCHASE_REPORTS: '/purchased-goods/reports',
  PURCHASE_REPORT: '/purchased-goods/reports/:report_uuid',

  // new report
  PURCHASE_REPORT_CREATE: '/purchase-reports/new',
  PURCHASE_REPORT_CREATE_SETUP: '/purchase-reports/new/setup',
  PURCHASE_REPORT_CREATE_METRIC: '/purchase-reports/new/metric',
  PURCHASE_REPORT_EDIT_SETUP: '/purchase-reports/:report_uuid/edit/setup',
  PURCHASE_REPORT_EDIT_METRIC: '/purchase-reports/:report_uuid/edit/metric',
  PURCHASE_REPORT_PUBLISH: '/purchased-goods/reports/:report_uuid/publish',

  PURCHASE_REPORT_DOWNLOAD: '/purchased-goods/reports/:report_uuid/download',

  // food and drink
  PURCHASED_FOOD_AND_DRINK: '/purchased-goods/food-and-drink',
  PURCHASED_FOOD_AND_DRINK_DATA_COVERAGE:
    '/purchased-goods/food-and-drink/data-coverage',
  PURCHASED_FOOD_AND_DRINK_REVIEW: '/purchased-goods/food-and-drink/review',
  PURCHASED_FOOD_AND_DRINK_INGREDIENTS:
    '/purchased-goods/food-and-drink/ingredients',
  PURCHASED_FOOD_AND_DRINK_ITEM: '/purchased-goods/food-and-drink/:item_uuid',
  PURCHASED_FOOD_AND_DRINK_ITEM_MODELLING:
    '/purchased-goods/food-and-drink/:item_uuid/modelling',
  PURCHASED_FOOD_AND_DRINK_ITEM_PURCHASES:
    '/purchased-goods/food-and-drink/:item_uuid/purchases',
  PURCHASED_FOOD_AND_DRINK_ITEM_SPEND:
    '/purchased-goods/food-and-drink/:item_uuid/spend',

  // packaging
  PURCHASED_PACKAGING: '/purchased-goods/packaging',
  PURCHASED_PACKAGING_DATA_COVERAGE: '/purchased-goods/packaging/data-coverage',
  PURCHASED_PACKAGING_ITEM: '/purchased-goods/packaging/:item_uuid',
  PURCHASED_PACKAGING_ITEM_MODELLING:
    '/purchased-goods/packaging/:item_uuid/modelling',
  PURCHASED_PACKAGING_ITEM_PURCHASES:
    '/purchased-goods/packaging/:item_uuid/purchases',
  PURCHASED_PACKAGING_ITEM_SPEND: '/purchased-goods/packaging/:item_uuid/spend',
};

export function PurchasedGoodsRoutes() {
  return (
    <Route
      element={<CustomRoute feature={[FEATURES.scope3]} element={<Outlet />} />}
    >
      <Route
        path={purchasedGoodsRoutes.PURCHASED_INSIGHTS}
        element={<PurchasingInsightsPage />}
      />
      <Route
        path={purchasedGoodsRoutes.PURCHASED_GOODS_IMPORTS_LIST}
        element={<PurchasedGoodsImportsListPage />}
      />

      {/* Unified import flow. All purchased-goods imports route here: a single
          landing → content types → organise → upload wizard sharing one
          ImportLayout React Hook Form context. */}
      <Route
        element={
          <CustomRoute
            element={
              <ImportLayout
                importType="food"
                onClosePath={purchasedGoodsRoutes.PURCHASED_GOODS_IMPORTS_LIST}
                reportType={OperationReportTypeEnum.purchase}
              />
            }
            hasSidebar={false}
          />
        }
      >
        <Route
          path={purchasedGoodsRoutes.PURCHASED_GOODS_IMPORT}
          element={<GoodsImportIntroPage />}
        />
        <Route
          path={purchasedGoodsRoutes.PURCHASED_GOODS_IMPORT_TYPES}
          element={<GoodsImportTypePage />}
        />
        <Route
          path={purchasedGoodsRoutes.PURCHASED_GOODS_IMPORT_ORGANISE}
          element={<GoodsImportConfigurePage />}
        />
      </Route>

      <Route path={purchasedGoodsRoutes.PURCHASED_EXCLUDED_ITEMS}>
        {/* Landing page, then a standalone subpage per category. */}
        <Route index element={<ExcludedItemsLandingPage />} />
        <Route
          path={purchasedGoodsRoutes.PURCHASED_EXCLUDED_ITEMS_REVIEW}
          element={<ExcludedItemsCategoryPage type="review" />}
        />
        <Route
          path={purchasedGoodsRoutes.PURCHASED_EXCLUDED_ITEMS_OTHER}
          element={<ExcludedItemsCategoryPage type="other" />}
        />
      </Route>

      <Route
        path={purchasedGoodsRoutes.PURCHASED_FOOD_AND_DRINK}
        element={<PurchasedFoodAndDrinkLayout />}
      >
        <Route
          path={purchasedGoodsRoutes.PURCHASED_FOOD_AND_DRINK}
          index
          element={<PurchasedFoodAndDrinkListPage />}
        />
        <Route
          path={purchasedGoodsRoutes.PURCHASED_FOOD_AND_DRINK_DATA_COVERAGE}
          element={<PurchasedFoodAndDrinkDataCoveragePage />}
        />
        <Route
          path={purchasedGoodsRoutes.PURCHASED_FOOD_AND_DRINK_REVIEW}
          element={<PurchasedFoodAndDrinkReviewPage />}
        />
        <Route
          path={purchasedGoodsRoutes.PURCHASED_FOOD_AND_DRINK_INGREDIENTS}
          element={<PurchasedFoodAndDrinkIngredientsPage />}
        />
      </Route>

      <Route
        path={purchasedGoodsRoutes.PURCHASED_FOOD_AND_DRINK_ITEM}
        element={<PurchasedFoodAndDrinkItemContext />}
      >
        <Route path="" element={<PurchasedFoodAndDrinkItemLayout />}>
          <Route index element={<PurchasedFoodAndDrinkItemOverviewPage />} />
          <Route
            path={purchasedGoodsRoutes.PURCHASED_FOOD_AND_DRINK_ITEM_MODELLING}
            element={<PurchasedFoodAndDrinkItemPage />}
          />
          <Route
            path={purchasedGoodsRoutes.PURCHASED_FOOD_AND_DRINK_ITEM_PURCHASES}
            element={<PurchasedFoodAndDrinkItemPurchasesPage />}
          />
          <Route
            path={purchasedGoodsRoutes.PURCHASED_FOOD_AND_DRINK_ITEM_SPEND}
            element={
              <CustomRoute
                feature={[FEATURES.cost]}
                element={<PurchasedFoodAndDrinkItemSpendPage />}
              />
            }
          />
        </Route>
      </Route>

      <Route
        path={purchasedGoodsRoutes.PURCHASED_PACKAGING}
        element={<PurchasedPackagingLayout />}
      >
        <Route
          path={purchasedGoodsRoutes.PURCHASED_PACKAGING}
          index
          element={<PurchasedPackagingListPage />}
        />
        <Route
          path={purchasedGoodsRoutes.PURCHASED_PACKAGING_DATA_COVERAGE}
          element={<PurchasedPackagingDataCoveragePage />}
        />
      </Route>
      <Route
        path={purchasedGoodsRoutes.PURCHASED_PACKAGING_ITEM}
        element={<PurchasedPackagingItemContext />}
      >
        <Route path="" element={<PurchasedPackagingItemLayout />}>
          <Route index element={<PurchasedPackagingItemOverviewPage />} />
          <Route
            path={purchasedGoodsRoutes.PURCHASED_PACKAGING_ITEM_MODELLING}
            element={<PurchasedPackagingItemPage />}
          />
          <Route
            path={purchasedGoodsRoutes.PURCHASED_PACKAGING_ITEM_PURCHASES}
            element={<PurchasedPackagingItemPurchasesPage />}
          />
          <Route
            path={purchasedGoodsRoutes.PURCHASED_PACKAGING_ITEM_SPEND}
            element={
              <CustomRoute
                feature={[FEATURES.cost]}
                element={<PurchasedPackagingItemSpendPage />}
              />
            }
          />
        </Route>
      </Route>

      {/* New report  */}
      <Route
        element={
          <CustomRoute
            hasSidebar={false}
            element={
              <CreateReportLayout
                reportType={OperationReportTypeEnum.purchase}
                onClosePath={purchasedGoodsRoutes.PURCHASE_REPORTS}
              />
            }
          />
        }
      >
        <Route
          path={purchasedGoodsRoutes.PURCHASE_REPORT_CREATE}
          element={<CreateReportIntroPage />}
        />
        <Route
          path={purchasedGoodsRoutes.PURCHASE_REPORT_CREATE_SETUP}
          element={<CreateReportSetupPage />}
        />
        <Route
          path={purchasedGoodsRoutes.PURCHASE_REPORT_CREATE_METRIC}
          element={<CreateReportMetricPage />}
        />
      </Route>

      {/* Edit report */}
      <Route
        element={
          <CustomRoute
            hasSidebar={false}
            element={
              <CreateReportLayout
                reportType={OperationReportTypeEnum.purchase}
                onClosePath={purchasedGoodsRoutes.PURCHASE_REPORTS}
              />
            }
          />
        }
      >
        <Route
          path={purchasedGoodsRoutes.PURCHASE_REPORT_EDIT_SETUP}
          element={<CreateReportSetupPage />}
        />
        <Route
          path={purchasedGoodsRoutes.PURCHASE_REPORT_EDIT_METRIC}
          element={<CreateReportMetricPage />}
        />
      </Route>

      <Route
        path={purchasedGoodsRoutes.PURCHASE_REPORT_PUBLISH}
        element={
          <CustomRoute
            hasSidebar={false}
            element={
              <CreateReportPublishPage
                reportType={OperationReportTypeEnum.purchase}
              />
            }
          />
        }
      />

      {/* Reports */}
      <Route
        path={purchasedGoodsRoutes.PURCHASE_REPORTS}
        element={<PurchaseReportsListPage />}
      />

      <Route
        path={purchasedGoodsRoutes.PURCHASE_REPORT}
        element={
          <CustomRoute
            hasSidebar={false}
            element={<PurchaseReportContextProvider />}
          />
        }
      >
        <Route index element={<PurchaseReport />} />
        <Route
          path={purchasedGoodsRoutes.PURCHASE_REPORT_DOWNLOAD}
          element={<PurchaseReportDownload />}
        />
      </Route>
    </Route>
  );
}
